import { useCallback, useEffect, useRef, useState } from 'react';
import { getSupabaseClient } from '../supabase-client';
import { reportError } from '../../lib/monitoring';

/**
 * THE SAVED REPORTS OF ONE PROJECT — M10 / S10-05.
 *
 * **METADATA ONLY, AND THAT IS A PERFORMANCE RULE WITH TEETH.** The list must
 * never pull `snapshot` — a frozen report can carry every Capture title and
 * every photo key it contained — and must never touch Storage. A list that
 * downloaded a PDF to draw a row would cost megabytes per screen on a phone.
 *
 * **`select('*')` IS FORBIDDEN**, here and by a test: a star select would start
 * shipping the snapshot the moment the column list changed, invisibly.
 *
 * **ONLY `ready` ROWS ARE REPORTS.** A `pending` row is a save that never
 * completed; it is not history, it is not shown, and it is not counted.
 */

/** The exact columns the list needs. Nothing wider, and never a star. */
export const SAVED_REPORT_LIST_COLUMNS =
  'id, created_at, report_date, page_count, filename, byte_size' as const;

export interface SavedReportListItem {
  readonly id: string;
  readonly created_at: string;
  readonly report_date: string;
  readonly page_count: number;
  readonly filename: string;
  readonly byte_size: number;
}

export type SavedReportsStatus = 'loading' | 'ready' | 'failed';

export interface SavedReportsState {
  readonly status: SavedReportsStatus;
  readonly reports: readonly SavedReportListItem[];
  readonly error: string | null;
  reload(): Promise<void>;
}

export function useSavedReports(projectId: string): SavedReportsState {
  const client = getSupabaseClient();
  const [status, setStatus] = useState<SavedReportsStatus>(client ? 'loading' : 'failed');
  const [reports, setReports] = useState<readonly SavedReportListItem[]>([]);
  const [error, setError] = useState<string | null>(
    client ? null : 'Crewlink is not connected to a server yet.',
  );

  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const fetchReports = useCallback(async (): Promise<
    { kind: 'ready'; rows: SavedReportListItem[] } | { kind: 'failed' }
  > => {
    if (!client) return { kind: 'failed' };
    const { data, error: queryError } = await client
      .from('saved_report')
      .select(SAVED_REPORT_LIST_COLUMNS)
      .eq('project_id', projectId)
      // Durability, not workflow: an unfinished save is not a saved report.
      .eq('state', 'ready')
      // Newest first, by the row's own durable time — never by the printed
      // date string, which is display text and does not sort.
      .order('created_at', { ascending: false });

    if (queryError) {
      reportError(queryError, 'saved-reports.load');
      return { kind: 'failed' };
    }
    return { kind: 'ready', rows: data ?? [] };
  }, [client, projectId]);

  const apply = useCallback(
    (result: { kind: 'ready'; rows: SavedReportListItem[] } | { kind: 'failed' }) => {
      if (!alive.current) return;
      if (result.kind === 'failed') {
        setReports([]);
        // **AN EMPTY LIST AND A FAILED LOAD ARE DIFFERENT FACTS.** Showing
        // "no saved reports" when the read failed would tell Brandon his
        // history is gone.
        setError('Could not load your saved reports.');
        setStatus('failed');
        return;
      }
      setReports(result.rows);
      setError(null);
      setStatus('ready');
    },
    [],
  );

  /** Only the newest read may speak — the house pattern. */
  const generation = useRef(0);

  const load = useCallback(async (): Promise<void> => {
    generation.current += 1;
    const ticket = generation.current;
    const result = await fetchReports();
    if (ticket !== generation.current) return;
    apply(result);
  }, [fetchReports, apply]);

  useEffect(() => {
    void load();
  }, [load]);

  const reload = useCallback(async () => {
    await load();
  }, [load]);

  return { status, reports, error, reload };
}

/** One saved report, read for its detail screen. Snapshot included here. */
export const SAVED_REPORT_DETAIL_COLUMNS =
  'id, created_at, report_date, page_count, filename, byte_size, project_id, owner_user_id, snapshot, snapshot_version' as const;
